import { useState, type InputHTMLAttributes } from 'react';
import { cx } from './cx.js';

/**
 * A chip thumb in a shallow groove, with the part behind it filled in.
 *
 * A range input has no shared shape across browsers: WebKit styles
 * `::-webkit-slider-thumb` and `::-webkit-slider-runnable-track`, Firefox
 * styles `::-moz-range-thumb` and `::-moz-range-track`, and neither inherits
 * anything from the other. That is roughly thirty declarations to say one
 * thing, which is why writing them inline meant writing them twice — and the
 * two copies had already drifted apart: settings got a bordered track with a
 * pressed-in floor and a shadow under the thumb, the replay scrubber got a
 * plain bar and a flat thumb, and the volume slider and the scrubber stopped
 * looking like the same control.
 *
 * The vendor internals live together in ui/css/primitives.css. A new slider
 * gets `className` for where it sits, never for what it looks like.
 *
 * The native input keeps its keyboard, pointer and form behavior. A sibling
 * SVG draws the spent track using the live value as rectangle geometry, so
 * both browser track pseudo-elements can stay transparent. No browser-specific
 * progress pseudo or authored style declaration is needed.
 */

export interface SliderProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'type' | 'style'
> {
  /** Layout only — order in a flex row, how much of it to take. */
  readonly className?: string | undefined;
}

/* Native track/thumb geometry lives in primitives.css; the input keeps its 48px hit area. */
const SLIDER =
  'slider-input m-0 h-12 cursor-pointer appearance-none bg-transparent disabled:cursor-default';

/**
 * Where the thumb is, 0 to 100, from whatever `min`, `max` and `value` say.
 *
 * A range input's defaults are the ones HTML gives it, not the ones a caller
 * happens to pass, so they are written here rather than assumed. Anything that
 * does not come out a number — the `readonly string[]` an input value is
 * allowed to be, a `max` below its `min` — reads as empty, because a fill of
 * `NaN%` is a track painted edge to edge.
 */
function fillPercent(
  value: InputHTMLAttributes<HTMLInputElement>['value'],
  min: InputHTMLAttributes<HTMLInputElement>['min'],
  max: InputHTMLAttributes<HTMLInputElement>['max'],
): number {
  const low = Number(min ?? 0);
  const high = Number(max ?? 100);
  const at = Number(value ?? low);
  if (!Number.isFinite(at) || !Number.isFinite(low) || !(high > low)) return 0;
  return Math.min(100, Math.max(0, ((at - low) / (high - low)) * 100));
}

export function Slider({ className, onChange, ...rest }: SliderProps): React.JSX.Element {
  /*
   * A controlled slider already remembers where its thumb is — the caller re-
   * renders it with a new `value` on every move. An uncontrolled one moves
   * without a render at all, so the fill would freeze at whatever
   * `defaultValue` said. This is that memory, and only that: `value` wins over
   * it whenever there is one, so a controlled slider is still driven entirely
   * by its prop and this state is never read.
   */
  const [moved, setMoved] = useState<string | undefined>(undefined);
  const percent = fillPercent(rest.value ?? moved ?? rest.defaultValue, rest.min, rest.max);

  return (
    <span className={cx('relative flex items-center has-disabled:opacity-40', className)}>
      <svg
        className="pointer-events-none absolute inset-x-0 h-2 w-full overflow-hidden rounded-full bg-ground-dark"
        aria-hidden="true"
        preserveAspectRatio="none"
      >
        <rect className="fill-gray-700" width={`${String(percent)}%`} height="100%" />
      </svg>
      <input
        type="range"
        className={cx(SLIDER, 'relative w-full')}
        onChange={(event) => {
          if (rest.value === undefined) setMoved(event.target.value);
          onChange?.(event);
        }}
        {...rest}
      />
    </span>
  );
}
